import { useState } from "react";

import dompurify from "isomorphic-dompurify";

import { PackageCrossIcon } from "@jti/brand-icons/package-cross";
import { Box } from "@jti/core-components/components/Box";
import { Swatch } from "@jti/core-components/components/Swatch";
import { type StockStatusElements } from "@jti/fe-common";
import { DEPRECATED_Loading } from "@jti/ui/components/DEPRECATED_Loading";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { getOptionsForSwatchById } from "@product-details/utils/getOptionsForSwatch";

// TODO: add types / DESP-44757
export type ProductBundlesBlockType = {
  currentProduct: any;
  title: string;
  options: any;
  stockStatusElements: StockStatusElements;
  onChange: (data: any) => void;
  showValidation: boolean;
  isLoading: boolean;
};

const { sanitize } = dompurify;

const ProductBundlesBlock = ({
  currentProduct,
  title,
  options,
  stockStatusElements,
  onChange,
  showValidation,
  isLoading,
}: ProductBundlesBlockType) => {
  const { __ } = useTranslations();
  const hasAvailableOptions = options?.length > 0;
  const [isOutOfStockSelected, setIsOutOfStockSelected] = useState(false);

  const onChangeSelection = (data: any) => {
    onChange(data);
    setIsOutOfStockSelected(data?.isInStock === false);
  };

  let finalTitle = title;

  if (currentProduct?.label && hasAvailableOptions) {
    finalTitle += `: ${currentProduct.label}`;
  }

  const hasError = isOutOfStockSelected || (showValidation && !currentProduct);

  return (
    <Box title={finalTitle} hasError={hasError}>
      {isLoading && <DEPRECATED_Loading />}
      <div
        aria-invalid={hasError}
        aria-label={finalTitle}
        className={"product-color-container"}
        data-testid={"product-color-selector"}
      >
        {hasAvailableOptions && currentProduct?.product?.thumbnail?.url && (
          <img
            className={"product-color-container__image"}
            src={currentProduct.product.thumbnail.url}
            alt={currentProduct?.label ?? ""}
          />
        )}
        {hasAvailableOptions && !isLoading && (
          <Swatch
            aria-label={finalTitle}
            data-testid="test-color-selector"
            items={getOptionsForSwatchById(options, true, stockStatusElements).map(
              ({ color, label, value, isDisabled }) => ({
                color,
                label,
                value,
                isOutOfStock: isDisabled,
              })
            )}
            value={currentProduct?.swatchData}
            onChange={(item) => {
              const fullOption = getOptionsForSwatchById(options, true, stockStatusElements).find(
                (o) => o.value === item.value
              );

              if (fullOption) {
                onChangeSelection(fullOption);
              }
            }}
          />
        )}
        {!hasAvailableOptions && (
          <>
            <PackageCrossIcon />
            <span
              className="product-bundle-block__out-of-stock"
              dangerouslySetInnerHTML={{ __html: sanitize(__("product.label.outofstock")) }}
            ></span>
          </>
        )}
      </div>
    </Box>
  );
};

export default ProductBundlesBlock;
